import React, { useState, useRef, useEffect } from "react";
import { Table } from "react-bootstrap";
import { lightTheme } from "../Constants/Colors";
import { fontFamily, fontSize, fontWeight } from "../Constants/Fonts";

// Per-cell text handling (applies to plain-string cell values only — nodes like
// badges/buttons render untouched):
//   • short text → stays on a single line
//   • longer text → flows onto a second line, then clamps at 2 lines with "…"
//   • if it overflows those 2 lines → click the cell to expand/collapse the rest
const CELL_ONE_LINE = 40; // strings this short never need wrapping — render as-is
const CELL_MAX_WIDTH = 320;

function ExpandableCell({ value }) {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const ref = useRef(null);

    // Detect whether the (collapsed) text actually exceeds 2 lines so we only
    // show the expand affordance when there's hidden content.
    useEffect(() => {
        const el = ref.current;
        if (el && !expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [value, expanded]);

    // Only plain strings get the length rules; any React node renders as-is.
    if (typeof value !== 'string') return value;
    // Short — inherit the cell's nowrap so it stays on one line.
    if (value.length <= CELL_ONE_LINE) return value;

    const base = { maxWidth: CELL_MAX_WIDTH, whiteSpace: 'normal', wordBreak: 'break-word', verticalAlign: 'top' };
    const style = expanded
        ? { ...base, display: 'inline-block' }
        : { ...base, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
    const clickable = overflowing || expanded;

    return (
        <span
            ref={ref}
            onClick={clickable ? (e) => { e.stopPropagation(); setExpanded(v => !v); } : undefined}
            title={!expanded && overflowing ? value : undefined}
            style={{ ...style, cursor: clickable ? 'pointer' : undefined }}
        >
            {value}
        </span>
    );
}

const CommonTable = ({ headers, rows, actions, type, noPadding, noScroll, onRowClick }) => {
    // noScroll → let the parent container handle overflow; useful inside
    // modals so we don't get a scrollbar-inside-a-scrollbar.
    const wrapClass = noScroll
        ? ''
        : (type === 'finding' ? '' : (rows?.length <= 6 ? 'max_content_height' : 'wrp_table'));

    // Full-width span for an expanded detail row (see `row.expandedContent`).
    const colSpan = (headers?.length || (rows?.[0]?.data?.length || 1)) + (actions && !headers ? 1 : 0);

    return (
        <div
            className={`cmn_table_scroll ${wrapClass}`}
            // Scroll horizontally when the table is wider than its container
            // (many-column tables) instead of compressing / wrapping cells.
            style={{ padding: 0, overflowX: 'auto' }}
        >
            <Table
                className="cstm_table"
                style={{
                    borderRadius: '12px',
                    borderSpacing: 0,
                    borderCollapse: 'collapse',
                    overflow: 'hidden'
                }} >
                <thead style={{ position: 'sticky', top: '0', zIndex: '9' }}>
                    <tr>
                        {headers?.map((header, index) => (
                            <th key={index} style={{ backgroundColor: lightTheme.surfaceMuted, color: lightTheme.textMuted, fontFamily, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody
                >
                    {rows?.length === 0 ? (
                        <tr>
                            <td colSpan={headers?.length} style={{ textAlign: "center", backgroundColor: lightTheme.surface }}>
                                <span style={{ color: lightTheme.textSecondary }}> No data found</span>
                            </td>
                        </tr>
                    ) : (
                        rows?.map((row, index) => (
                            <React.Fragment key={index}>
                            <tr
                                onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                                style={{ backgroundColor: lightTheme.surface, color: lightTheme.textPrimary, cursor: onRowClick ? 'pointer' : undefined, ...(row?.rowStyle || {}) }}
                            >
                                {row?.data.map((col, idx) => {
                                    // Handle skip flag for rowSpan cells
                                    if (col && typeof col === 'object' && col.skip) {
                                        return null;
                                    }

                                    // Handle rowSpan object
                                    if (col && typeof col === 'object' && col.rowSpan !== undefined) {
                                        return (
                                            <td
                                                className={`${type === 'finding' ? 'finding_cstm_table' : ''}`}
                                                key={idx}
                                                style={{ backgroundColor: lightTheme.surface, color: lightTheme.textPrimary, verticalAlign: 'middle', ...col.style }}
                                                rowSpan={col.rowSpan}
                                            >
                                                {col.value}
                                            </td>
                                        );
                                    }

                                    // Normal cell. Default nowrap keeps node
                                    // cells (badges/buttons) on one line; string
                                    // cells get the length rules via ExpandableCell.
                                    return (
                                        <td className={`${type === 'finding' ? 'finding_cstm_table' : ''}`} key={idx} style={{ backgroundColor: lightTheme.surface, color: lightTheme.textPrimary, verticalAlign: 'middle', whiteSpace: 'nowrap' }}><ExpandableCell value={col} /></td>
                                    );
                                })}
                                {actions && (
                                    <td
                                        className={`${type === 'finding' ? 'finding_cstm_table' : ''}`}
                                        style={{ backgroundColor: lightTheme.surface, color: lightTheme.textPrimary, verticalAlign: 'middle', whiteSpace: 'nowrap' }}
                                    >
                                        {/* Row actions always sit on ONE row — never stack/wrap.
                                            ≤2 buttons render inline here; 3+ should be passed
                                            through CommonActionMenu to collapse into a ⋮ menu. */}
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
                                            {actions(row)}
                                        </div>
                                    </td>
                                )}
                            </tr>
                            {row?.expanded && row?.expandedContent != null && (
                                <tr>
                                    <td colSpan={colSpan} style={{ padding: 0, backgroundColor: lightTheme.surface }}>
                                        {row.expandedContent}
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                        ))
                    )}
                </tbody>
            </Table >
        </div >
    );
};

export default CommonTable;
